/**
 * Shared display helpers for voice metadata, used by both the customer-facing
 * voice picker and the internal voice admin page so labels never drift
 * between the two surfaces.
 */
import {
  GENDER_OPTIONS,
  AGE_GROUP_OPTIONS,
  TONE_OPTIONS,
  PRICING_TIER_OPTIONS,
} from '@/lib/voiceConstants'
import type { Voice } from '@/types/assistant'

/**
 * Fallback formatter for meta values without a curated i18n label:
 * `warm_friendly` -> `Warm Friendly`.
 */
export function formatMetaLabel(value: string | null | undefined): string {
  if (!value) return ''
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Human-readable provider name shown in voice bylines and badges.
// Maps the backend's lowercase enum value to a brand-styled label.
const PROVIDER_LABELS: Record<string, string> = {
  elevenlabs: 'ElevenLabs',
  azure: 'Azure',
  mistral: 'Mistral',
  gemini: 'Gemini',
  google: 'Google',
}

export function getProviderLabel(voice: Voice): string | null {
  const type = voice.provider?.provider_type?.toLowerCase()
  if (!type) return voice.provider?.name ?? null
  return PROVIDER_LABELS[type] ?? formatMetaLabel(type)
}

export type VoiceMetaKind = 'gender' | 'age_group' | 'tone' | 'pricing'

const META_OPTION_SETS: Record<VoiceMetaKind, { value: string; labelKey: string }[]> = {
  gender: GENDER_OPTIONS,
  age_group: AGE_GROUP_OPTIONS,
  tone: TONE_OPTIONS,
  pricing: PRICING_TIER_OPTIONS,
}

/** i18n key for a curated meta value, or undefined for unknown values. */
export function metaLabelKey(kind: VoiceMetaKind, value: string): string | undefined {
  return META_OPTION_SETS[kind].find((opt) => opt.value === value)?.labelKey
}

/**
 * Translate a meta value through the curated option sets, falling back to
 * title-casing for values the frontend does not know yet.
 * Pass i18next's `t` as the first argument.
 */
export function translateMetaValue(
  t: (key: string) => string,
  kind: VoiceMetaKind,
  value: string | null | undefined
): string {
  if (!value) return ''
  const key = metaLabelKey(kind, value)
  return key ? t(key) : formatMetaLabel(value)
}
